"use strict";
// C=Connect Event
// A=Authenticated Event
// D=Disconnect Event
// E=Reconnect Error Event
// P=Pong Event
// H=Heartbeat
// S=Sync process
// -=Offline
// x=Online
// R=Reload App
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.NetworkManager = void 0;
const Observable_1 = require("../socket/Observable");
const utils_1 = require("../utils");
const DomainEvents_1 = require("../utils/DomainEvents");
const OfflineTimeoutException_1 = require("../utils/OfflineTimeoutException");
const uuid_1 = require("uuid");
class NetworkManager {
    constructor(organizationId, socketAdapter, internetStatusManager) {
        this.socketAuthSuccessListeners = [];
        this.removeListeners = [];
        this.lastActiveSocketHeartbeat = -1;
        this.abortContextMap = new Map();
        this.organizationId = organizationId;
        this.socketAdapter = socketAdapter;
        this.internetStatusManager = internetStatusManager;
        this.syncMutex = (0, utils_1.createMutex)();
    }
    isSocketConnected() {
        return this.socketAdapter.isConnected();
    }
    updateLastSocketHeartbeat(heartbeat) {
        if (this.authenticatedSocketId) {
            this.lastActiveSocketHeartbeat = heartbeat;
        }
    }
    registerSocketAuthSuccessListener(cb) {
        const id = (0, uuid_1.v4)();
        this.socketAuthSuccessListeners.push({ id, cb });
        return () => {
            this.socketAuthSuccessListeners = this.socketAuthSuccessListeners.filter(listener => listener.id != id);
        };
    }
    init() {
        this.removeAllListeners();
        const socketHeartbeatListenerUnsubscribe = Observable_1.Observable.getInstance().subscribe((0, DomainEvents_1.getEventKey)(DomainEvents_1.SocketNetworkEvents.ON_PONG, this.organizationId), () => __awaiter(this, void 0, void 0, function* () {
            this.updateLastSocketHeartbeat((0, utils_1.currentEpoch)());
        }));
        const disconnectListenerUnsubscribe = Observable_1.Observable.getInstance().subscribe((0, DomainEvents_1.getEventKey)(DomainEvents_1.SocketNetworkEvents.ON_DISCONNECT, this.organizationId), (_a) => __awaiter(this, [_a], void 0, function* ({ socketId }) {
            if (!!this.authenticatedSocketId &&
                this.authenticatedSocketId !== socketId) {
                throw new Error(`Panic: disconnect received for socket ${socketId} but authenticatedSocketId is ${this.authenticatedSocketId} !!! ${this.organizationId}: ${JSON.stringify(this.abortContextMap)}`);
            }
            const abortContext = this.abortContextMap.get(socketId);
            if (abortContext) {
                abortContext.abort();
                this.abortContextMap.delete(socketId);
            }
            this.authenticatedSocketId = undefined;
        }));
        const didOfflineTimeout = () => {
            const currentTime = (0, utils_1.currentEpoch)();
            const offlineDuration = currentTime - this.lastActiveSocketHeartbeat;
            return {
                didTimeout: offlineDuration >
                    utils_1.HHNConstants.OFFLINE_TIMEOUT_DURATION_IN_MINS * 60000,
                offlineDuration,
            };
        };
        const authSuccessListenerUnsubscribe = Observable_1.Observable.getInstance().subscribe((0, DomainEvents_1.getEventKey)(DomainEvents_1.SocketNetworkEvents.ON_AUTHENTICATE_SUCCEEDED, this.organizationId), (_a) => __awaiter(this, [_a], void 0, function* ({ socketId }) {
            if (this.authenticatedSocketId) {
                throw new Error(`Panic: the socket is already in the authenticated status when a socket authentication success event is received !!! ${this.organizationId}: ${socketId}, authenticatedSocketId: ${this.authenticatedSocketId}: : ${JSON.stringify(this.abortContextMap)}`);
            }
            const heartbeat = (0, utils_1.currentEpoch)();
            if (this.lastActiveSocketHeartbeat === -1) {
                this.authenticatedSocketId = socketId;
                this.updateLastSocketHeartbeat(heartbeat);
                return;
            }
            this.abortContextMap.forEach(context => context.abort());
            this.abortContextMap.clear();
            const { didTimeout, offlineDuration } = didOfflineTimeout();
            if (didTimeout) {
                throw new OfflineTimeoutException_1.OfflineTimeoutException(`User offline for: ${offlineDuration / 60000} minutes`);
            }
            const localAbortContext = new utils_1.AbortContext();
            this.abortContextMap.set(socketId, localAbortContext);
            try {
                yield this.syncMutex((abortContext) => __awaiter(this, void 0, void 0, function* () {
                    yield this.internetStatusManager.waitForOnline(abortContext);
                    if (abortContext === null || abortContext === void 0 ? void 0 : abortContext.isAborted)
                        return;
                    const results = yield Promise.allSettled(this.socketAuthSuccessListeners.map((l) => __awaiter(this, void 0, void 0, function* () {
                        yield l.cb(this.lastActiveSocketHeartbeat, abortContext);
                    })));
                    const failures = results.filter((r) => r.status === "rejected");
                    if (failures.length > 0) {
                        throw failures[0].reason;
                    }
                }), localAbortContext);
                if (!localAbortContext.isAborted) {
                    this.authenticatedSocketId = socketId;
                    this.updateLastSocketHeartbeat(heartbeat);
                }
            }
            catch (e) {
                throw new utils_1.SyncException(e);
            }
            finally {
                this.abortContextMap.delete(socketId);
            }
        }));
        this.removeListeners.push(socketHeartbeatListenerUnsubscribe);
        this.removeListeners.push(authSuccessListenerUnsubscribe);
        this.removeListeners.push(disconnectListenerUnsubscribe);
    }
    destroy() {
        this.removeAllListeners();
    }
    removeAllListeners() {
        this.removeListeners.forEach(removeListener => removeListener());
        this.removeListeners = [];
    }
}
exports.NetworkManager = NetworkManager;
